/**
 * Locale ⇄ URL-path helpers for the per-locale documentation routes (ADR-0075).
 *
 * URL convention:
 *   - English is unprefixed: `/docs/introduction` (the `x-default`).
 *   - Every other locale is prefixed: `/<locale>/docs/introduction`
 *     (e.g. `/es/docs/introduction`, `/ar/docs/...`).
 *
 * Applies site-wide: every marketing route is published per-locale (GA-13 /
 * ADR-0133, widening the docs-only ADR-0075). These helpers are pure (no React,
 * no `window`) so the prerender script, the SSR entry, and components can all
 * import them.
 */
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from '@syncanix/shared';

/** First path segment, or `''` for `/` / empty. */
function firstSegment(pathname: string): string {
  return pathname.split('/').find((s) => s.length > 0) ?? '';
}

/**
 * Resolve the locale a path is published under. A leading non-English supported
 * locale segment wins; anything else is the English default. (English never
 * carries a prefix, so `/en/...` is NOT treated as English here — it isn't a
 * path we emit.)
 */
export function localeFromPath(pathname: string): Locale {
  const seg = firstSegment(pathname);
  if (seg !== DEFAULT_LOCALE && isSupportedLocale(seg)) {
    return seg;
  }
  return DEFAULT_LOCALE;
}

/**
 * Strip a leading non-English locale prefix, returning the English-relative path
 * (always `/`-rooted). `/es/docs/x` → `/docs/x`; `/docs/x` → `/docs/x`.
 */
export function stripLocalePrefix(pathname: string): string {
  const seg = firstSegment(pathname);
  if (seg !== DEFAULT_LOCALE && isSupportedLocale(seg)) {
    const rest = pathname.slice(`/${seg}`.length);
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * Compose the published path for an English-relative path under `locale`.
 * English is returned unchanged; other locales gain the `/<locale>` prefix.
 * `localizedPath('/docs/x', 'fr')` → `/fr/docs/x`.
 */
export function localizedPath(enRelativePath: string, locale: Locale): string {
  const path = enRelativePath.startsWith('/') ? enRelativePath : `/${enRelativePath}`;
  if (locale === DEFAULT_LOCALE) return path;
  // The home path localizes to the bare locale segment ('/es'), never '/es/', so
  // the localized home canonical matches the trailing-slash-free English home.
  return path === '/' ? `/${locale}` : `/${locale}${path}`;
}
